import { Card } from "@/app/admin/_common/ui/card";
import { Skeleton } from "@/app/admin/_common/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/app/admin/_common/ui/tooltip";
import { cn } from "@/app/_utils/cn";
import { TrendingUp, TrendingDown, Info } from "lucide-react";

export default function KpiCard({
  label,
  value,
  trendPct,
  icon: Icon,
  isLoading,
  hasTrend,
  tooltip,
  valueTone = "neutral",
}: {
  label: string;
  value: string;
  trendPct: number | null;
  icon: React.ComponentType<{ className?: string }>;
  isLoading?: boolean;
  /** Whether this card will render a trend line once loaded — used for the loading skeleton, since `trendPct` is still null before the async trend calc resolves. Defaults to `trendPct !== null`. */
  hasTrend?: boolean;
  /** Optional explainer shown via an info icon next to the label. */
  tooltip?: string;
  /** Colours the figure itself. Only for cards where a sign genuinely means
   *  good or bad; a cost is not "good" for being large. */
  valueTone?: "neutral" | "signed";
}) {
  const showTrendSkeleton = hasTrend ?? trendPct !== null;
  // Read the sign off the rendered string so "$-47.00" and "-6.6%" both work
  // without every caller passing the raw number alongside the label.
  const signed = valueTone === "signed" && /\d/.test(value);
  const negative = signed && value.includes("-");
  const positive = signed && !negative && parseFloat(value.replace(/[^0-9.]/g, "")) > 0;
  return (
    <Card className="rounded-md p-3.5 shadow-none">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground">
          {label}
          {tooltip && (
            <TooltipProvider>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Info className="size-3 flex-none cursor-help text-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[240px] text-[12px]">{tooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
        <Icon className="size-4 text-muted-foreground/60" />
      </div>
      {isLoading ? (
        <>
          <Skeleton className={cn("h-6 w-20", showTrendSkeleton && "mb-2")} />
          {showTrendSkeleton && <Skeleton className="hidden h-3.5 w-28 sm:block" />}
        </>
      ) : (
        <>
          <div
            className={cn(
              "text-[21px] font-bold leading-none tracking-tight",
              negative ? "text-red-600" : positive ? "text-green-600" : "text-foreground"
            )}
          >
            {value}
          </div>
          {trendPct !== null && (
            <div className="mt-2 hidden items-center gap-1 text-[11.5px] sm:flex">
              <span
                className={cn(
                  "flex items-center gap-0.5 font-medium",
                  trendPct >= 0 ? "text-green-600" : "text-red-600"
                )}
              >
                {trendPct >= 0 ? (
                  <TrendingUp className="size-3.5" />
                ) : (
                  <TrendingDown className="size-3.5" />
                )}
                {trendPct >= 0 ? "+" : ""}
                {trendPct.toFixed(1)}%
              </span>
              <span className="text-muted-foreground">from last week</span>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
